import {
  getComplaintTipConfig,
  getRiskLevelColor,
  getRiskLevelText,
  RISK_LEVEL_NONE
} from './complaintRisk'

const EMPTY_TEXT = '--'

export function toDisplayValue(value, fallback = EMPTY_TEXT) {
  return value === undefined || value === null || value === '' ? fallback : value
}

export function preferLabel(label, value, fallback = EMPTY_TEXT) {
  return toDisplayValue(label || value, fallback)
}

// 字段更新标记：newFieldCodes 为本次画像生成中有更新的接口字段名集合，
export function createIsNewFieldChecker(newFieldCodes) {
  const codes = new Set(Array.isArray(newFieldCodes) ? newFieldCodes : [])
  return (...fields) => fields.some(field => codes.has(field))
}

function isValidDisplayValue(value) {
  return value !== undefined && value !== null && value !== '' && value !== EMPTY_TEXT
}

function createReasonModal(label, reason) {
  return {
    modalTitle: `${label}原因`,
    modalContent: toDisplayValue(reason)
  }
}

function normalizeDiagnosisItems(value) {
  if (!isValidDisplayValue(value)) return []
  if (Array.isArray(value)) {
    return value
      .map(item => {
        if (typeof item === 'string') return item.trim()
        if (item && typeof item === 'object') {
          const label = item.label || item.value || item.name || item.diagnosisName
          return typeof label === 'string' ? label.trim() : ''
        }
        return ''
      })
      .filter(Boolean)
  }
  if (typeof value !== 'string') return []
  return value
    .split(/[，,；;、\n]/)
    .map(item => item.trim())
    .filter(Boolean)
}

export function createDiseaseDiagnosisList(data = {}) {
  const diagnosisParts = []
  diagnosisParts.push(...normalizeDiagnosisItems(data.diseaseDiagnosisPrimary))
  if (isValidDisplayValue(data.diseaseDiagnosisPatientSelfReport)) {
    diagnosisParts.push(`患者自述：${data.diseaseDiagnosisPatientSelfReport}`)
  }
  return diagnosisParts
}

export function createDiseaseDiagnosisText(data = {}) {
  const diagnosisParts = createDiseaseDiagnosisList(data)
  return diagnosisParts.join('，')
}

// 投诉风险视图数据：入口条目展示值 + 弹窗透传数据
export function createComplaintRiskView(data = {}, isNew) {
  const tendency = data.complaintTendency || {}
  const current = tendency.current || {}
  const records = Array.isArray(tendency.records) ? tendency.records : []
  // complaintTendency 或其 current 缺失（或等级未知）时按 NONE 档兜底展示
  const hasRiskData = Boolean(data.complaintTendency && data.complaintTendency.current)
  const riskLevel = hasRiskData ? current.riskLevel : RISK_LEVEL_NONE

  return {
    // 入口条目：无记录时仅展示风险文字，不可点击
    riskLabel:
      current.riskLabel || getRiskLevelText(riskLevel) || getRiskLevelText(RISK_LEVEL_NONE),
    riskColor: getRiskLevelColor(riskLevel) || getRiskLevelColor(RISK_LEVEL_NONE),
    clickable: records.length > 0,
    isNew: isNew('complaintTendency'),
    // 提示条配置：等级未知时为 null，弹窗内隐藏提示条
    tipConfig: getComplaintTipConfig(riskLevel),
    records
  }
}

export function createPortraitViewModel(data = {}, options = {}) {
  const formatMedicationEndTime =
    typeof options.formatMedicationEndTime === 'function'
      ? options.formatMedicationEndTime
      : () => EMPTY_TEXT
  const isNew = createIsNewFieldChecker(data.newFieldCodes)
  const diseaseDiagnosisList = createDiseaseDiagnosisList(data)

  return {
    summary: [
      {
        label: '复诊情况',
        value: preferLabel(data.followUpCountLabel, data.followUpCountValue),
        isNew: isNew('followUpCountLabel', 'followUpCountValue')
      },
      {
        label: '当前状态',
        value: toDisplayValue(data.currentStatusContext),
        isNew: isNew('currentStatus', 'currentStatusContext')
      },
      {
        label: '关键风险',
        value: toDisplayValue(data.keyRiskLabel),
        warn: true,
        isNew: isNew('keyRiskLabel')
      },
      {
        label: '待办事项',
        value: toDisplayValue(data.todoItems),
        isNew: isNew('todoItems')
      },
      {
        label: '建议动作',
        value: toDisplayValue(data.suggestedAction),
        good: true,
        isNew: isNew('suggestedAction')
      }
    ],
    stateItems: [
      {
        label: '复诊情况',
        value: preferLabel(data.followUpCountLabel, data.followUpCountValue),
        tooltip: '患者在该医生下的累计有效复诊次数',
        isNew: isNew('followUpCountLabel', 'followUpCountValue')
      },
      {
        label: '本次用药状态',
        value: preferLabel(
          data.currentCourseMedicationDaysLabel,
          data.currentCourseMedicationDaysValue
        ),
        warn: true,
        isNew: isNew('currentCourseMedicationDaysLabel', 'currentCourseMedicationDaysValue')
      }
    ],
    communicationItems: [
      {
        label: '沟通配合度',
        value: preferLabel(data.communicationComplianceLabel, data.communicationComplianceValue),
        isNew: isNew('communicationComplianceLabel', 'communicationComplianceValue'),
        ...createReasonModal('沟通配合度', data.communicationComplianceReason)
      },
      {
        label: '触达偏好',
        value: preferLabel(data.contactPreferenceLabel, data.contactPreferenceValue),
        isNew: isNew('contactPreferenceLabel', 'contactPreferenceValue'),
        ...createReasonModal('触达偏好', data.contactPreferenceReason)
      },
      {
        label: '响应特点',
        value: preferLabel(data.responsePatternLabel, data.responsePatternValue),
        isNew: isNew('responsePatternLabel', 'responsePatternValue'),
        ...createReasonModal('响应特点', data.responsePatternReason)
      },
      {
        label: '情绪状态',
        value: preferLabel(data.emotionStateLabel, data.emotionStateValue),
        isNew: isNew('emotionStateLabel', 'emotionStateValue'),
        ...createReasonModal('情绪状态', data.emotionStateReason)
      }
    ],
    diagnosisItems: [
      {
        label: '病情诊断',
        value: toDisplayValue(diseaseDiagnosisList.join('，')),
        values: diseaseDiagnosisList,
        modalTitle: '病情诊断',
        isNew: isNew('diseaseDiagnosisPrimary', 'diseaseDiagnosisPatientSelfReport')
      },
      {
        label: '用药结束时间',
        value: formatMedicationEndTime(data),
        warn: true,
        isNew: isNew('medicationEndDateValue', 'medicationEndDateDaysRemaining')
      }
    ],
    diagnosisMedicines: Array.isArray(data.currentMedication) ? data.currentMedication : [],
    historicalMedications: Array.isArray(data.historicalMedications)
      ? data.historicalMedications
      : [],
    selfReportedFeedbacks: Array.isArray(data.selfReportedFeedbacks)
      ? data.selfReportedFeedbacks
      : [],
    currentMedicationNew: isNew('currentMedication'),
    historicalMedicationsNew: isNew('historicalMedications'),
    selfReportedFeedbacksNew: isNew('selfReportedFeedbacks'),
    complaintRisk: createComplaintRiskView(data, isNew),
    newFieldCodes: Array.isArray(data.newFieldCodes) ? data.newFieldCodes : []
  }
}
